import { FunctionComponent, memo } from 'react'
import styled from 'styled-components'
import { useQueryParam } from 'use-query-params'
import { KolThumbnail } from '@/components/common/kol-thumbnail'
import PostCardStatistic from '@/components/common/post/post-card-statistic'
import UnsafeNewTabLink from '@/components/public-page/footer/components/unsafe-new-tab-link'
import routes from '@/hooks/use-authorization/routes'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Post } from '@/types/schema/api/radar-search-post'
import { convertPlatformType } from '@/utils/convert-platform'
import isNotNil from '@/utils/is-not-nil'

interface PostDetailProps {
  post?: Post
  className?: string
  from?: string
}

const PostDetail: FunctionComponent<PostDetailProps> = ({
  post,
  className,
  from,
}) => {
  const { formatDate } = useIntl()

  const [eventFrom] = useQueryParam<string>('from')

  if (!post) {
    return null
  }

  const {
    kol_uuid,
    kol_name,
    title,
    text,
    like_count,
    comment_count,
    share_count,
    view_count,
    play_count,
    platform_type,
    publish_time,
    is_disable_like,
    repost_and_quote_count,
  } = post

  return (
    <Wrapper className={className}>
      <KolInfo
        as={UnsafeNewTabLink}
        href={routes.kolId.stringifyUrl(
          {
            kolId: kol_uuid,
          },
          { from: from ?? eventFrom },
        )}
      >
        <KolThumbnail imageSize={48} kolUuid={kol_uuid} />
        <span>{kol_name}</span>
      </KolInfo>
      {isNotNil(publish_time) && (
        <PublishDate>{formatDate(publish_time)}</PublishDate>
      )}
      {isNotNil(title) && <Title>{title}</Title>}
      {isNotNil(text) && <Content>{text}</Content>}
      <PostCardStatistic
        commentCount={comment_count}
        isDisableLike={is_disable_like}
        likeCount={like_count}
        platformShortCode={convertPlatformType(platform_type)?.shortcode}
        playCount={play_count}
        repostAndQuoteCount={repost_and_quote_count}
        shareCount={share_count}
        viewCount={view_count}
      />
    </Wrapper>
  )
}

const KolInfo = styled.a`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 18px;
  font-weight: 700;
  pointer-events: none;

  &,
  &:hover {
    color: ${({ theme }): string => theme.colors.text.primary};
  }

  * {
    pointer-events: all;
  }
`

const PublishDate = styled.div`
  font-size: 12px;
  color: ${({ theme }): string => theme.colors.text.tooltip};
`

const Title = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }): string => theme.colors.text.primary};
`

const Content = styled.div`
  font-size: 14px;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const Wrapper = styled.div`
  padding: 16px 24px 0;
  padding-right: 16px;
  overflow: auto;

  ${KolInfo} {
    margin-bottom: 16px;
    margin-right: 28px; /* 防止撞到 x 按鈕 */
  }
  ${PublishDate} {
    margin-bottom: 8px;
  }
  ${Title} {
    margin-bottom: 4px;
  }
  ${Content} {
    margin-bottom: 8px;
  }
`

export default styled(memo(PostDetail))``
